/**
 * Export modal - handles format selection and export output
 *
 * Uses shared ExportFormatButtons and ExportPreview components.
 * Generates preview immediately on format/scope change.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileExport } from 'react-icons/fa';
import { IoFolderOpen, IoReader } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useFoldersQuery } from '@/features/import-export/hooks/api';
import type { EnrichedCollectionCard } from '@/types/card';
import { copyToClipboard, downloadExport, EXPORT_FORMATS, exportCollection, type ExportFormat } from '../exporters/registry';
import type { FormatOption } from '../types';
import ExportFormatButtons from './ExportFormatButtons';
import ExportPreview from './ExportPreview';

// ============================================================================
// Types
// ============================================================================

export interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  cards: EnrichedCollectionCard[];
}

type ExportScope = 'all' | 'folder';

// ============================================================================
// Constants
// ============================================================================

const FORMAT_OPTIONS: FormatOption<ExportFormat>[] = [
  {
    value: 'dragonshield',
    label: 'Dragon Shield CSV',
    description: 'Compatible with Dragon Shield MTG app',
    icon: <IoReader className="h-5 w-5" />,
  },
  {
    value: 'moxfield',
    label: 'Moxfield CSV',
    description: 'Import into Moxfield collection',
    icon: <IoReader className="h-5 w-5" />,
  },
  {
    value: 'mtga',
    label: 'MTGA Text',
    description: 'Simple text format for MTG Arena',
    icon: <IoReader className="h-5 w-5" />,
  },
  {
    value: 'mtgcraft',
    label: 'MtgCraft Backup',
    description: 'Full JSON backup with all metadata',
    icon: <FaFileExport className="h-5 w-5" />,
  },
];

const FORMAT_LABELS: Record<ExportFormat, string> = {
  dragonshield: 'Dragon Shield',
  moxfield: 'Moxfield',
  mtga: 'MTGA',
  mtgcraft: 'MtgCraft',
};

// ============================================================================
// Component
// ============================================================================

export default function ExportModal({ isOpen, onClose, cards }: ExportModalProps): ReactElement {
  const [format, setFormat] = useState<ExportFormat>('dragonshield');
  const [scope, setScope] = useState<ExportScope>('all');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [exportedData, setExportedData] = useState<string>('');

  const { data: folders = [] } = useFoldersQuery();

  // Build folder name map
  const folderNames = useMemo(() => {
    const map = new Map<string, string>();
    folders.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [folders]);

  // Filter cards based on scope
  const cardsToExport = useMemo(() => {
    if (scope === 'folder' && selectedFolderId) {
      return cards.filter((c) => c.folder_id === selectedFolderId);
    }
    return cards;
  }, [cards, scope, selectedFolderId]);

  // Generate export immediately when format/scope/cards change
  useEffect(() => {
    if (!isOpen || cardsToExport.length === 0) {
      setExportedData('');
      return;
    }

    try {
      const data = exportCollection(format, cardsToExport, folders, folderNames);
      setExportedData(data);
    } catch (error) {
      console.error('Export error:', error);
      setExportedData('');
    }
  }, [isOpen, format, cardsToExport, folders, folderNames]);

  const resetState = useCallback(() => {
    setFormat('dragonshield');
    setScope('all');
    setSelectedFolderId(null);
    setExportedData('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleDownload = useCallback(() => {
    if (!exportedData) return;

    const formatInfo = EXPORT_FORMATS.find((f) => f.value === format);
    const extension = formatInfo?.extension ?? 'txt';
    const mimeType = extension === 'json' ? 'application/json' : extension === 'csv' ? 'text/csv' : 'text/plain';
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `Collection - ${FORMAT_LABELS[format]} - ${dateStr}.${extension}`;

    downloadExport(exportedData, filename, mimeType);
  }, [exportedData, format]);

  const handleCopy = useCallback(async (): Promise<boolean> => {
    if (!exportedData) return false;

    try {
      await copyToClipboard(exportedData);
      return true;
    } catch {
      return false;
    }
  }, [exportedData]);

  const totalQuantity = cardsToExport.reduce((sum, c) => sum + c.quantity, 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Export Collection" size="lg">
      <div className="space-y-6">
        {/* Format selection */}
        <ExportFormatButtons options={FORMAT_OPTIONS} value={format} onChange={setFormat} columns={2} />

        {/* Scope selection */}
        <div className="space-y-3">
          <label className="text-pearl block text-sm font-medium">Export Scope</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setScope('all');
                setSelectedFolderId(null);
              }}
              className={clsx(
                'rounded-full px-4 py-2 text-sm transition-colors',
                scope === 'all' ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm',
              )}
            >
              All Cards ({cards.length})
            </button>
            {folders.length > 0 && (
              <button
                onClick={() => setScope('folder')}
                className={clsx(
                  'flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors',
                  scope === 'folder' ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm',
                )}
              >
                <IoFolderOpen className="h-4 w-4" />
                By Folder
              </button>
            )}
          </div>

          {/* Folder dropdown */}
          {scope === 'folder' && folders.length > 0 && (
            <select
              value={selectedFolderId ?? ''}
              onChange={(e) => setSelectedFolderId(e.target.value || null)}
              className="border-storm bg-slate text-pearl focus-ring w-full rounded-lg border px-4 py-2"
            >
              <option value="">Select a folder...</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name} ({cards.filter((c) => c.folder_id === folder.id).length} cards)
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Summary */}
        <div className="bg-slate/50 border-storm rounded-lg border p-3">
          <p className="text-silver text-sm">
            {cardsToExport.length} unique cards ({totalQuantity.toLocaleString()} total)
          </p>
        </div>

        {/* Preview with copy/download */}
        {exportedData ? (
          <ExportPreview content={exportedData} onCopy={handleCopy} onDownload={handleDownload} maxHeight="max-h-48" />
        ) : (
          <div className="border-storm bg-void/50 text-ash rounded-lg border p-8 text-center text-sm">
            {cardsToExport.length === 0 ? 'No cards to export' : 'Generating preview...'}
          </div>
        )}

        {/* Close button */}
        <div className="flex justify-end pt-2">
          <Button text="Close" variant="outlined" onClick={handleClose} />
        </div>
      </div>
    </Modal>
  );
}
